import React, { useState, useRef } from 'react';
import { uploadImageForInspection } from '../services/api';

function ImageUpload({ mode, onProcessingStart, onInspectionComplete, isProcessing }) {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/bmp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, WEBP, or BMP image.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be under 50MB.');
      return;
    }

    setError(null);
    setPreview(URL.createObjectURL(file));
    onProcessingStart();

    try {
      const result = await uploadImageForInspection(file, mode);
      onInspectionComplete(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed. Please try again.');
      onInspectionComplete(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  return (
    <div className="upload-container">
      <h2 className="section-title">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"/>
        </svg>
        Upload Image for Inspection
      </h2>
      <p className="section-desc">
        {mode === 'industry'
          ? 'Upload a factory floor or manufacturing site image for safety compliance analysis.'
          : 'Upload a construction site or public infrastructure image for regulatory compliance check.'}
      </p>

      <div
        className={`dropzone ${dragActive ? 'drag-active' : ''} ${isProcessing ? 'processing' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/bmp"
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        {isProcessing ? (
          <div className="processing-indicator">
            <div className="spinner"></div>
            <p>Running AI analysis...</p>
            <span className="processing-sub">Detecting objects, analyzing safety compliance</span>
          </div>
        ) : preview ? (
          <div className="preview-thumb">
            <img src={preview} alt="Upload preview" />
            <p>Click or drop to analyze another image</p>
          </div>
        ) : (
          <div className="dropzone-content">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="6" y="10" width="36" height="28" rx="4"/>
              <path d="M6 34l10-10 6 6 8-8 12 12"/>
              <circle cx="16" cy="20" r="3"/>
            </svg>
            <p className="drop-text">Drag & drop an image here</p>
            <span className="drop-sub">or click to browse — JPEG, PNG, WEBP, BMP up to 50MB</span>
          </div>
        )}
      </div>

      {error && (
        <div className="upload-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.5v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
}

export default ImageUpload;
